/*
 * cf-section-add-remove.js — Click Fencing — 2026-09-07
 *
 * Pete: "i also want you to put a delete section and add section button on all the
 * calculators in the sections panel for configure sections."
 *
 * WHY IT NEEDS ITS OWN IMPLEMENTATION
 * -----------------------------------
 * The only existing way to change the section count is the dropdown on step 1, and its
 * handler is destructive: handleSectionsChange() does `this.state.sections = {}` and
 * rebuilds every section blank (flat-top.js:4223). Wiring buttons straight to it would
 * throw away everything the customer had typed.
 *
 * createSectionForm() also builds a BLANK form — the length input carries no value and the
 * panel select defaults to 2400 — so the DOM does not repopulate itself from state either.
 *
 * THE APPROACH
 * ------------
 * Snapshot every section's state in the order we want, let the calculator do its own full
 * rebuild for the new count, then write the snapshot back and sync the controls to match.
 *
 * The rebuild is deliberately not avoided. Per-section listeners are re-bound by
 * generateSectionForms(), and the delegated change handler is keyed on element id
 * (`this.changeHandlers[e.target.id]`, flat-top.js:3983), so letting the calculator
 * re-create the forms means every handler ends up bound to the right section number.
 * Renumbering the existing DOM instead would leave listeners pointing at their old
 * indices — some are delegated by id, but others (rake fields, clamp fields) are bound
 * per element, so a renumber would half-work, which is worse than not working.
 *
 * State is assigned directly and the controls are then set WITHOUT dispatching events:
 * the state is already correct, so firing change would only re-run solvers, re-run the
 * snap, and risk a popup for each section restored.
 *
 * Delete removes the section the customer is looking at and closes the gap, so section 3
 * of 4 becomes section 3 of 3 and nothing is stranded. Add appends and jumps to it.
 * The cap matches the step-1 dropdown: 1-10 on all four calculators (raised from 6 on 2026-09-11:
 * Dallas Shores, the first real fence sale, needed 8 sections).
 */
(function () {
  "use strict";

  if (!/(blade-fence-calculator|flat-top-fence-calculator|perfmesh-fence|glass-fencing)/.test(location.pathname)) return;
  if (window.__cfSectionAddRemove) return;
  window.__cfSectionAddRemove = true;

  var TRIES = 0, MAX_TRIES = 200;
  // 10 on all four (raised 2026-09-11). PerfMesh was held at 6 the same day because past 6
  // sections its quote filed parts under the wrong section. That was never a section-count
  // limit: cf-perfmesh-cut-parts.js took the first 2m kit in the whole job when moving a small
  // panel onto the 1m kit, and it reproduced at 2 sections (1800, 3600). Fixed there, verified
  // at 1-10 sections, and templates/page.perfmesh-calc.liquid now offers 7-10 as well.
  var MAX_SECTIONS = 10, MIN_SECTIONS = 1;

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }
  function el(id) { return document.getElementById(id); }

  function setVal(id, v) {
    var e = el(id);
    if (!e || v === undefined || v === null) return;
    try { e.value = String(v); } catch (err) {}
  }
  function setRadio(name, v) {
    if (v === undefined || v === null) return;
    try {
      var rs = document.getElementsByName(name);
      for (var i = 0; i < rs.length; i++) rs[i].checked = (String(rs[i].value) === String(v));
    } catch (err) {}
  }
  function setCheck(id, on) {
    var e = el(id);
    if (e) e.checked = !!on;
  }
  function showFields(id, on) {
    var e = el(id);
    if (e) e.classList.toggle("hidden", !on);
  }

  // Put one section's saved state back on screen. Guarded field by field: the four
  // calculators do not all have the same controls (glass has a gap select, flat top has a
  // cut-side select, only some have clamps).
  function paint(n, s) {
    if (!s) return;
    setVal("section-" + n + "-length", s.length);
    setVal("section-" + n + "-panel", s.panelLength);
    setVal("section-" + n + "-gap", s.gapSpacing);
    setVal("section-" + n + "-end-post-setup", s.endPostSetup);
    setVal("cf-cut-side-" + n, s.cutSide);
    setRadio("section-" + n + "-panel-mode", s.panelMode);
    setRadio("section-" + n + "-color", s.color);
    setRadio("section-" + n + "-ground-post-type", s.groundPost ? "inground" : "base");

    setCheck("section-" + n + "-raked", s.hasRaked);
    showFields("section-" + n + "-raked-fields", s.hasRaked);
    setVal("section-" + n + "-retained-height", s.retainedHeight);
    setRadio("section-" + n + "-raked-side", s.rakedSide);

    setCheck("section-" + n + "-gate", s.hasGate);
    showFields("section-" + n + "-gate-fields", s.hasGate);
    setVal("section-" + n + "-gate-length", s.gateLength);
    setRadio("section-" + n + "-gate-side", s.gateSide);
    setVal("section-" + n + "-hinge-color", s.hingeType);
    setVal("section-" + n + "-latch-type", s.latchType);

    setCheck("section-" + n + "-clamp", s.hasClamp);
    showFields("section-" + n + "-clamp-fields", s.hasClamp);
    setVal("section-" + n + "-clamp-position", s.clampPosition);
    setVal("section-" + n + "-clamp-color", s.clampColor);
  }

  // Rebuild for `list.length` sections and restore them in that order.
  function rebuild(fc, list, showIndex) {
    var count = list.length;

    // Keep the step-1 dropdown honest, or going Back would disagree with what is drawn.
    var sel = el("sections-count");
    if (sel) { try { sel.value = String(count); } catch (e) {} }

    // The calculator's own rebuild: fresh forms, fresh listeners, correct indices.
    fc.handleSectionsChange(count);

    for (var i = 0; i < count; i++) {
      var n = i + 1;
      fc.state.sections[n] = list[i];
      paint(n, list[i]);
    }

    try { fc.generateSectionTabs(); } catch (e) {}
    addButtons(fc);

    var target = Math.max(1, Math.min(showIndex || 1, count));
    try { fc.showSection(target); } catch (e) {}

    // Recalculate every section that has a length, so drawings and prices match state.
    for (var k = 1; k <= count; k++) {
      if (fc.state.sections[k] && fc.state.sections[k].length) {
        try { fc.updateCalculationResults(k); } catch (e) {}
      }
    }
  }

  function currentList(fc) {
    var out = [], n = fc.state.sectionsCount || 0;
    for (var i = 1; i <= n; i++) out.push(fc.state.sections[i] || {});
    return out;
  }

  function addSection(fc) {
    var list = currentList(fc);
    if (list.length >= MAX_SECTIONS) return;
    // Same shape the calculator seeds a new section with, so nothing downstream sees a
    // half-built object. handleSectionsChange will have created a blank for this slot;
    // we take that blank rather than inventing one.
    list.push(null);
    var showIdx = list.length;
    var count = list.length;
    var sel = el("sections-count");
    if (sel) { try { sel.value = String(count); } catch (e) {} }
    fc.handleSectionsChange(count);
    for (var i = 0; i < count - 1; i++) {
      var n = i + 1;
      if (list[i]) { fc.state.sections[n] = list[i]; paint(n, list[i]); }
    }
    try { fc.generateSectionTabs(); } catch (e) {}
    addButtons(fc);
    try { fc.showSection(showIdx); } catch (e) {}
    for (var k = 1; k < count; k++) {
      if (fc.state.sections[k] && fc.state.sections[k].length) {
        try { fc.updateCalculationResults(k); } catch (e) {}
      }
    }
  }

  function deleteSection(fc, n) {
    var list = currentList(fc);
    if (list.length <= MIN_SECTIONS) return;
    list.splice(n - 1, 1);
    rebuild(fc, list, Math.min(n, list.length));
  }

  function mkBtn(label, title, bg, onClick) {
    var b = document.createElement("button");
    b.type = "button";
    b.className = "cf-section-mgmt";
    b.textContent = label;
    b.title = title;
    b.style.cssText =
      "padding:8px 14px;font-size:0.95rem;border-radius:6px;cursor:pointer;" +
      "border:1px solid " + bg + ";background:#fff;color:" + bg + ";font-weight:600;white-space:nowrap;";
    b.addEventListener("click", function (e) { e.preventDefault(); onClick(); });
    return b;
  }

  // generateSectionTabs() rewrites the container, so the buttons are re-added after it.
  function addButtons(fc) {
    try {
      var host = el("section-tabs-container");
      if (!host) return;
      var wrap = host.firstElementChild || host;
      if (wrap.querySelector(".cf-section-mgmt")) return;

      var count = fc.state.sectionsCount || 0;
      var cur = fc.state.currentSection || 1;

      var bar = document.createElement("div");
      bar.style.cssText = "display:flex;gap:8px;align-items:center;margin-left:auto;";

      if (count < MAX_SECTIONS) {
        bar.appendChild(mkBtn("+ Add section", "Add another section to this fence", "#059669",
          function () { addSection(inst()); }));
      }
      if (count > MIN_SECTIONS) {
        bar.appendChild(mkBtn("Delete section " + cur, "Remove this section and close the gap", "#b91c1c",
          function () {
            var fc2 = inst();
            var n = fc2.state.currentSection || 1;
            var s = fc2.state.sections[n];
            var hasData = s && s.length;
            if (hasData && !window.confirm("Delete section " + n + "? Anything entered for it will be lost.")) return;
            deleteSection(fc2, n);
          }));
      }
      wrap.appendChild(bar);
    } catch (e) {
      try { console.warn("[cf-section-add-remove] addButtons failed:", e && e.message); } catch (e2) {}
    }
  }

  function install() {
    var fc = inst();
    if (!fc || !fc.state) return false;
    var P = Object.getPrototypeOf(fc);
    if (!P || P.__cfSectionAddRemoveInstalled) return true;

    if (typeof P.generateSectionTabs === "function") {
      var oTabs = P.generateSectionTabs;
      P.generateSectionTabs = function () {
        var r = oTabs.apply(this, arguments);
        try { addButtons(this); } catch (e) {}
        return r;
      };
    }
    // The label says which section Delete will remove, so it follows the active tab.
    if (typeof P.showSection === "function") {
      var oShow = P.showSection;
      P.showSection = function (n) {
        var r = oShow.apply(this, arguments);
        try {
          var host = el("section-tabs-container");
          var bar = host && host.querySelector(".cf-section-mgmt");
          if (bar && bar.parentNode) bar.parentNode.remove();
          addButtons(this);
        } catch (e) {}
        return r;
      };
    }

    P.__cfSectionAddRemoveInstalled = true;
    addButtons(fc);
    try { console.log("[cf-section-add-remove] installed on " + location.pathname); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {}
    if (!done && ++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryInstall);
  } else {
    tryInstall();
  }
})();
